import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCycleSnapshots, type CycleSnapshotMeta } from '@/api/lockedSummary';
import { TransientLoadNotice } from '@/components/TransientLoadNotice';
import { isTransientApiError, transientReadRetryOptions } from '@/lib/transientApiErrors';
import { useParams } from 'wouter';
import { FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/tables/data-table';
import { createCycleSnapshotColumns, CycleSnapshot } from '@/components/tables/cycle-snapshot-columns';

// NOTE: This page displays campaign snapshots contained in a locked cycle.
// Backend still uses /api/cycle-snapshots endpoints; frontend renames for clarity.

export default function CycleSnapshotsListPage() {
  const params = useParams<{ cycleId: string }>();
  const cycleId = params?.cycleId;
  const {
    data = [],
    isLoading,
    isFetching,
    error,
    failureCount,
    refetch,
  } = useQuery<CycleSnapshot[]>({
    queryKey: ['cycleSnapshots', cycleId],
    queryFn: async () => {
      const snapshots = await getCycleSnapshots(cycleId!);
      return snapshots.map((snapshot: CycleSnapshotMeta) => ({
        id: snapshot.id,
        cycleId: snapshot.cycleId,
        createdAt: snapshot.createdAt,
        createdByUserId: Number(snapshot.createdByUserId),
        note: snapshot.note ?? null,
      }));
    },
    enabled: !!cycleId,
    ...transientReadRetryOptions,
  });

  const formatDate = useCallback((date: string | Date) => {
    return new Date(date).toLocaleString();
  }, []);

  const columns = useMemo(
    () => createCycleSnapshotColumns({ cycleId: cycleId!, formatDate }),
    [cycleId, formatDate]
  );

  if (!cycleId) return <div className="p-4">Invalid cycle.</div>;
  if (isLoading || (isFetching && failureCount > 0)) {
    return (
      <div className="p-4">
        {failureCount > 0 ? 'System is busy — retrying snapshot list…' : 'Loading…'}
      </div>
    );
  }

  if (error && isTransientApiError(error)) {
    return (
      <div className="p-4">
        <TransientLoadNotice
          message="Could not load campaign snapshots because the server is temporarily busy."
          onRetry={() => { void refetch(); }}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-red-600">Failed to load campaign snapshots.</div>;
  }

  if (data.length === 0) return <div className="p-4 text-sm text-gray-600">No campaign snapshots yet.</div>;

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Campaign Snapshots (Cycle {cycleId})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data}
            searchKey="note"
            searchPlaceholder="Search by note..."
            showColumnToggle={true}
          />
        </CardContent>
      </Card>
    </div>
  );
}
